/**
 * PlayerInfo - InfoActor subclass for replicating per-player state.
 * Server spawns one per connected player. Clients can query all PlayerInfo actors for player lists, scoreboards, etc.
 */

import { EngineClass } from '../systems/ClassRegistry.js';
import { property } from '../utils/serialization/decorator.js';

import { InfoActor } from './InfoActor.js';

import type { ActorOptions } from './Actor.js';

@EngineClass('PlayerInfo')
export class PlayerInfo extends InfoActor {
  /** Unique client ID for this player */
  @property({ default: '', replicate: true })
  public clientId: string = '';

  /**
   * Server-only identity token supplied by the client during the join handshake.
   * Used to match a reconnecting client to their preserved `PlayerInfo`.
   * Not replicated — never sent to other clients.
   */
  public identityToken: string = '';

  /** Player's display name */
  @property({ default: 'Player', replicate: true })
  public playerName: string = 'Player';

  /** Whether the player has marked themselves as ready (for lobby/ready-up systems) */
  @property({ default: false, replicate: true })
  public isReady: boolean = false;

  /** Player's team assignment (e.g., 'red', 'blue', 'spectator', or empty for FFA) */
  @property({ default: '', replicate: true })
  public team: string = '';

  /** Player's current score */
  @property({ default: 0, replicate: true, net: { netType: 'int' } })
  public score: number = 0;

  /** Player's current ping in milliseconds */
  @property({ default: 0, replicate: true, net: { netType: 'uint' } })
  public ping: number = 0;

  /** Whether the player is currently alive (for respawn systems) */
  @property({ default: true, replicate: true })
  public isAlive: boolean = true;

  /** Player's current kill count (for deathmatch modes) */
  @property({ default: 0, replicate: true, net: { netType: 'uint' } })
  public kills: number = 0;

  /** Player's current death count (for deathmatch modes) */
  @property({ default: 0, replicate: true, net: { netType: 'uint' } })
  public deaths: number = 0;

  public override initialize(options?: ActorOptions): void {
    super.initialize(options);

    // Enable replication for this actor
    this.replicated = true;
  }

  public onRepReady(): void {
    this.onReadyStateChanged(this.isReady, this.isReady);
  }

  /**
   * Called on clients when ready state changes. Override to trigger UI updates.
   * @param oldReady - Previous ready state
   * @param newReady - New ready state
   */
  protected onReadyStateChanged(oldReady: boolean, newReady: boolean): void {
    console.log(`[PlayerInfo] Player ${this.playerName} ready state: ${oldReady} → ${newReady}`);

    // Example: Play sound effect, update UI, etc.
    // This is where you'd hook into your game's UI system
  }

  public getKDRatio(): number {
    return this.deaths > 0 ? this.kills / this.deaths : this.kills;
  }
}
